import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";
import { shareEvent } from "@/lib/native";

const BANNER = "/photos/prieure-sunset.jpg";

function formatEventDateRange(e, locale, t) {
  const opts = { weekday: "long", day: "numeric", month: "long", year: "numeric" };
  const loc = locale === "fr" ? "fr-FR" : "en-GB";
  const start = new Date(e.date).toLocaleDateString(loc, opts);
  const timeStr = e.time ? ` — ${t.events.atTime} ${e.time}${e.end_time ? `–${e.end_time}` : ""}` : "";
  if (e.end_date && e.end_date !== e.date) {
    const end = new Date(e.end_date).toLocaleDateString(loc, opts);
    return `${locale === "fr" ? "Du" : "From"} ${start} ${locale === "fr" ? "au" : "to"} ${end}${timeStr}`;
  }
  return `${start}${timeStr}`;
}

function recurrenceLabel(e, locale) {
  const until = e.recurrence_until
    ? new Date(e.recurrence_until).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;
  if (e.recurrence === "daily") {
    return locale === "fr"
      ? (until ? `TOUS LES JOURS · jusqu'au ${until}` : "TOUS LES JOURS")
      : (until ? `DAILY · until ${until}` : "DAILY");
  }
  if (e.recurrence === "weekly") {
    const weekday = new Date(e.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "long" });
    return locale === "fr"
      ? (until ? `TOUS LES ${weekday.toUpperCase()}S · jusqu'au ${until}` : `TOUS LES ${weekday.toUpperCase()}S`)
      : (until ? `EVERY ${weekday.toUpperCase()} · until ${until}` : `EVERY ${weekday.toUpperCase()}`);
  }
  return "";
}

function multiDayLabel(e, locale) {
  const start = new Date(e.date);
  const end = new Date(e.end_date);
  const days = Math.round((end - start) / 86400000) + 1;
  return locale === "fr" ? `SUR ${days} JOURS` : `OVER ${days} DAYS`;
}

/**
 * Generic events listing page — used by /offices, /concerts, /conferences,
 * /autres and the legacy /evenements route. The category prop drives the
 * server filter and the hero copy.
 */
export default function Events({ category = null }) {
  const { t, locale } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = category ? `/events?category=${category}` : "/events";
    setLoading(true);
    api.get(url).then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, [category]);

  const copy = (() => {
    if (!category) {
      return { kicker: t.events.kicker, title: t.events.title, empty: t.events.empty };
    }
    const pageT = t.eventsByCategory?.[category] || {};
    return {
      kicker: pageT.kicker || t.events.kicker,
      title: pageT.title || t.events.title,
      empty: pageT.empty || t.events.empty,
    };
  })();

  return (
    <div data-testid={`events-page-${category || "all"}`}>
      <section className="relative" style={{ height: "42vh", minHeight: 320 }}>
        <img src={BANNER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.72rem] text-white/90 mb-4">{copy.kicker}</div>
          <h1 className="font-display text-5xl sm:text-7xl text-white">{copy.title}</h1>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prieure">
          <Ornament className="mb-12" />
          <div className="flex justify-end mb-8 -mt-6">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/events.ics${category ? `?category=${category}` : ""}`}
              data-testid={`ical-subscribe-${category || "all"}`}
              className="btn btn-outline text-xs"
              title={t.events.icalHint}
            >
              {t.events.subscribeIcal}
            </a>
          </div>
          {loading ? (
            <p className="font-body text-center" style={{ color: "var(--muted)" }}>{t.common.loading}</p>
          ) : events.length === 0 ? (
            <p data-testid="events-empty" className="font-display italic text-2xl text-center"
               style={{ color: "var(--muted)" }}>
              {copy.empty}
            </p>
          ) : (
            <div className="space-y-16">
              {events.map((e, i) => (
                <article
                  key={e.id}
                  data-testid={`event-${e.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
                >
                  <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <Link to={`/evenements/${e.id}`} className="block overflow-hidden">
                      <img src={e.image_url} alt="" className="w-full object-cover hover:scale-105 transition-transform duration-700"
                           style={{ aspectRatio: "4/3" }} />
                    </Link>
                  </div>
                  <div className="md:col-span-7">
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <span className="font-caps text-[0.72rem]" style={{ color: "var(--cinnabar)" }}>
                        {formatEventDateRange(e, locale, t)}
                      </span>
                      {e.ticketed && (
                        <span className="tag tag-cinnabar">
                          {t.events.ticketedTag}
                        </span>
                      )}
                      {e.recurrence && e.recurrence !== "none" && (
                        <span className="tag tag-gold" data-testid={`event-recurrence-${e.id}`}>
                          {recurrenceLabel(e, locale)}
                        </span>
                      )}
                      {e.end_date && e.end_date !== e.date && !e.recurrence_until && (
                        <span className="tag tag-gold" data-testid={`event-multiday-${e.id}`}>
                          {multiDayLabel(e, locale)}
                        </span>
                      )}
                      {(pickLocalized(e.title, locale) || "").toLowerCase().includes("ostension") && (
                        <span className="tag tag-gold">UNESCO</span>
                      )}
                    </div>
                    <h2 className="font-display text-4xl leading-tight mb-3">
                      {pickLocalized(e.title, locale)}
                    </h2>
                    <p className="font-body text-base mb-2" style={{ color: "var(--ink-2)" }}>
                      📍 {pickLocalized(e.location, locale)}
                    </p>
                    <p className="font-body text-lg mt-4" style={{ color: "var(--ink-2)" }}>
                      {pickLocalized(e.description, locale)}
                    </p>
                    <div className="mt-6 flex gap-4 flex-wrap">
                      <Link to={`/evenements/${e.id}`} data-testid={`event-details-${e.id}`} className="btn btn-outline">
                        {t.events.details}
                      </Link>
                      {e.ticketed && (
                        <Link to={`/evenements/${e.id}`} data-testid={`event-book-${e.id}`} className="btn btn-primary">
                          {t.common.bookTickets} — {e.price_eur}{t.common.euro}
                        </Link>
                      )}
                      <button
                        type="button"
                        data-testid={`event-share-${e.id}`}
                        onClick={async () => {
                          const ok = await shareEvent(e, locale);
                          if (ok && !navigator.share) {
                            // Copy-fallback happened — tell the user
                            // Using a soft in-place hint avoids overlays.
                            const el = document.querySelector(`[data-testid="event-share-${e.id}"]`);
                            if (el) {
                              const prev = el.textContent;
                              el.textContent = locale === "fr" ? "✓ Lien copié" : "✓ Link copied";
                              setTimeout(() => { el.textContent = prev; }, 1800);
                            }
                          }
                        }}
                        className="btn btn-ghost"
                        title={locale === "fr" ? "Partager cet événement" : "Share this event"}
                      >
                        {locale === "fr" ? "Partager" : "Share"}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
